import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, ZAR } from "@/lib/api";
import { motion } from "framer-motion";
import { ArrowDownLeft, ArrowUpRight, Receipt, FileDown } from "lucide-react";

const filters = [
  { key: "all", label: "All" },
  { key: "purchase", label: "Purchases" },
  { key: "repayment", label: "Repayments" },
];

const monthOptions = Array.from({ length: 6 }).map((_, i) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - i);
  return {
    value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
    label: d.toLocaleDateString("en-ZA", { month: "long", year: "numeric" }),
  };
});

export default function Transactions() {
  const [txns, setTxns] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(monthOptions[0].value);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/transactions");
      setTxns(data.transactions);
      setLoading(false);
    })();
  }, []);

  const downloadStatement = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/statements?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `haki-statement-${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Statement downloaded");
    } catch {
      toast.error("Could not generate statement");
    } finally {
      setDownloading(false);
    }
  };

  const list = txns.filter((t) => (filter === "all" ? true : t.type === filter));
  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
      return iso;
    }
  };

  return (
    <div>
      <p className="text-xs font-medium tracking-overline text-primary uppercase mb-2">History</p>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6">Transactions</h1>

      <div className="border-2 border-border rounded-2xl bg-card p-4 mb-8 flex items-center justify-between gap-4 flex-wrap" data-testid="statement-bar">
        <div className="flex items-center gap-3">
          <FileDown className="text-primary" size={20} />
          <div>
            <p className="text-sm font-medium">Monthly statement</p>
            <p className="text-xs text-muted-foreground">Download a PDF of your spending, points and repayments.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="statement-month-select"
            className="bg-secondary border border-border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
          >
            {monthOptions.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <button
            onClick={downloadStatement}
            disabled={downloading}
            data-testid="statement-download-btn"
            className="px-4 py-2 rounded-full text-sm font-semibold bg-primary text-white inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform duration-150 disabled:opacity-60"
          >
            <FileDown size={15} /> {downloading ? "Generating…" : "Download PDF"}
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {filters.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`txn-filter-${f.key}`}
            className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-colors duration-150 ${
              filter === f.key ? "border-primary bg-primary text-white" : "border-border hover:border-primary"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="border-2 border-border rounded-2xl bg-card overflow-hidden" data-testid="transactions-list">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : list.length === 0 ? (
          <div className="p-10 text-center">
            <Receipt className="mx-auto text-muted-foreground mb-3" size={28} />
            <p className="text-sm text-muted-foreground">No transactions to show.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {list.map((t, i) => {
              const isRepay = t.type === "repayment";
              return (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: Math.min(i * 0.03, 0.4) }}
                  className="flex items-center gap-4 p-4"
                  data-testid={`txn-row-${t.id}`}
                >
                  <div className={`w-10 h-10 flex items-center justify-center shrink-0 ${isRepay ? "bg-secondary text-muted-foreground" : "bg-primary/10 text-primary"}`}>
                    {isRepay ? <ArrowUpRight size={18} /> : <ArrowDownLeft size={18} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{t.description}</p>
                    <p className="text-xs text-muted-foreground font-mono truncate">
                      {t.merchant} · {t.reference} · {fmtDate(t.created_at)}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`font-mono font-bold ${isRepay ? "text-muted-foreground" : "text-primary"}`}>
                      {isRepay ? "-" : "+"}{ZAR(t.amount)}
                    </p>
                    {t.points_earned > 0 && (
                      <p className="text-xs text-muted-foreground font-mono">+{t.points_earned} pts</p>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
